import _ from 'lodash';
import { Tooltip, notification } from 'antd';
import { KeyTypes, PromotionTypes } from './Enums';
import { Location } from 'react-router-dom';
import {
    AreaChartOutlined,
    BorderOuterOutlined,
    FilterFilled,
    SlidersOutlined,
    MoneyCollectOutlined,
    DollarOutlined,
    BoxPlotOutlined,
    BuildOutlined,
    BlockOutlined,
    ClusterOutlined,
    ProjectOutlined,
    ExperimentOutlined,
    LineChartOutlined,
    ExpandOutlined,
    UngroupOutlined,
    GoldOutlined,
    ToTopOutlined,
    CloseCircleFilled,
    CheckCircleFilled,
    ExclamationCircleFilled,
    DownOutlined,
    LockOutlined,
} from '@ant-design/icons';
import { messageConfig } from 'config/appMessageConfig';
import dayjs from 'dayjs';
import { KpiTypeId, LenseConfig, Project, Scenario, tabsType } from 'types/pricingSimulator';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { OfferMethod, OfferTypeUnit } from 'types/tradePromoOptimization';
import { colorConstants } from 'config/colorConstants';
dayjs.extend(utc);
dayjs.extend(timezone);
import { ReactNode } from 'react';
import { Auth0DecodedHash } from 'auth0-js';

export const getIconByName = (iconName: string) => {
    switch (iconName) {
        case 'AreaChartOutlined':
            return <AreaChartOutlined />;
        case 'BorderOuterOutlined':
            return <BorderOuterOutlined />;
        case 'FilterFilled':
            return <FilterFilled />;
        case 'SlidersOutlined':
            return <SlidersOutlined />;
        case 'MoneyCollectOutlined':
            return <MoneyCollectOutlined />;
        case 'DollarOutlined':
            return <DollarOutlined />;
        case 'BoxPlotOutlined':
            return <BoxPlotOutlined />;
        case 'BuildOutlined':
            return <BuildOutlined />;
        case 'BlockOutlined':
            return <BlockOutlined />;
        case 'ClusterOutlined':
            return <ClusterOutlined />;
        case 'ProjectOutlined':
            return <ProjectOutlined />;
        case 'ExperimentOutlined':
            return <ExperimentOutlined />;
        case 'LineChartOutlined':
            return <LineChartOutlined />;
        case 'ExpandOutlined':
            return <ExpandOutlined />;
        case 'GoldOutlined':
            return <GoldOutlined />;
        case 'UngroupOutlined':
            return <UngroupOutlined />;
        case 'ToTopOutlined':
            return <ToTopOutlined />;
        case 'DownOutlined':
            return <DownOutlined />;

        default:
            return <div></div>;
    }
};

//get max bound in graphs
export function getMaxBound(dataset: any, fields: any) {
    if (!Array.isArray(dataset) || !Array.isArray(fields)) return 0;
    const range = dataset.reduce((accu, data) => {
        const fieldsRange: any = fields.map((field) => {
            return typeof data[field] === 'number' ? data[field] : 0;
        });
        return [...accu, ...fieldsRange];
    }, []);

    return _.max(range);
}

//get min bound in graphs
export function getMinBound(dataset: any, fields: any) {
    if (!Array.isArray(dataset) || !Array.isArray(fields)) return 0;
    const range = dataset.reduce((accu, data) => {
        const fieldsRange = fields.map((field) => {
            return typeof data[field] === 'number' ? data[field] : 0;
        });
        return [...accu, ...fieldsRange];
    }, []);

    return _.min(range);
}

// Format number with K,M and B prefix
export const formatNumWithKMB = (number: number, decimalDigits: number = 1) => {
    return new Intl.NumberFormat('en-GB', {
        notation: 'compact',
        compactDisplay: 'short',
        maximumFractionDigits: decimalDigits,
    } as any).format(number);
};

// Format number with K,M and B prefix without default value
export const formatNumWithKMBAlt = (
    number: number,
    maxDecimalDigits?: number | undefined,
    minDecimalDigits?: number | undefined,
) => {
    const isNegative = number < 0;
    const absoluteNumber = Math.abs(number);

    const formattedNumber = new Intl.NumberFormat('en-GB', {
        notation: 'compact',
        compactDisplay: 'short',
        maximumFractionDigits: maxDecimalDigits,
        minimumFractionDigits: minDecimalDigits,
    }).format(absoluteNumber);

    return isNegative ? `-${formattedNumber}` : formattedNumber;
};

// Format number with K,M and B prefix without default value
export const formatNumWithKMBAlt2 = (
    number: number,
    maxDecimalDigits?: number | undefined,
    minDecimalDigits?: number | undefined,
) => {
    // If the number is too large or too small, use scientific notation
    if (number !== 0 && (Math.abs(number) >= 1e15 || Math.abs(number) < 1e-3)) {
        // Handle negative sign for very large/small numbers
        return `${number < 0 ? '-' : ''}${Math.abs(number).toExponential(2)}`;
    }

    // Use Intl.NumberFormat for compact formatting (K, M, B)
    const formattedNumber = new Intl.NumberFormat('en-GB', {
        notation: 'compact',
        compactDisplay: 'short',
        maximumFractionDigits: maxDecimalDigits,
        minimumFractionDigits: minDecimalDigits,
    }).format(Math.abs(number));

    // Add back negative sign if needed
    return number < 0 ? `-${formattedNumber}` : formattedNumber;
};
//remove function types from prevprops and nextprops
export const removeFunctionFromProps = (prevProps: any, nextProps: any, keyNamesToDelete: string[] = []) => {
    Object.keys(prevProps).map((item) => {
        if (typeof prevProps[item] === 'function' || keyNamesToDelete.includes(item)) {
            delete prevProps[item];
            delete nextProps[item];
        }
    });
    return [prevProps, nextProps];
};

//returns the offset width of the container
export const getContainerWidth = (containerId: string, buffer: number = 40) => {
    const mainContainerId = 'main-container';
    let container: any = document.getElementById(containerId);
    if (!container) {
        const mainContainer = document.getElementById(mainContainerId);
        container = { offsetWidth: mainContainer?.offsetWidth ? mainContainer.offsetWidth - buffer : undefined };
    }
    return container?.offsetWidth;
};

//calculates the main container width (main container width = total screen width - padding - margin - global filter width)
export const useMainContainerWidth = () => {
    const screenWidth = window.innerWidth;
    const padding = 16 * 2;
    const margin = 16 * 2;
    const globalFilterWidth = 212 + 20;
    return screenWidth - padding - margin - globalFilterWidth;
};

//to get config
export function getConfig(key: any, context: any) {
    return context?.[key];
}

//calculates the columnWidth depending on the screen size and column count
export const calculateScreenWidth = (
    width: number,
    columnCount: number,
    scale: number = 4,
    defaultColumnCount: number = 20,
    minWidth: number = 120,
) => {
    const screenWidth: number = width || window.innerWidth;
    const calculatedColumnWidth: number = Math.floor(screenWidth / columnCount);
    const defaultColumnWidth: number = Math.floor(screenWidth / defaultColumnCount) + scale;
    return Math.max(minWidth, calculatedColumnWidth);
    // return Math.min(defaultColumnWidth, calculatedColumnWidth);
};

export const trimStringToNewline = (string: string, limit: number, extraNewLine: number = 0) => {
    let res = '';
    if (extraNewLine) {
        res = string + '\n'.repeat(extraNewLine);
        return res;
    }
    for (let i = 0; i < string.length; i++) {
        if (i !== 0 && i % limit === 0) res += '-\n' + string[i];
        else res += string[i];
    }
    return res;
};
//to diaplay word accroding to standards
export function formatWord(words: string) {
    var separateWord = words.toLowerCase().replace(/_/g, ' ').split(' ');

    for (var i = 0; i < separateWord.length; i++) {
        separateWord[i] = separateWord[i].charAt(0).toUpperCase() + separateWord[i].substring(1);
    }
    return separateWord.join(' ');
}

//add 1 preceding 0 to a number
export function addPrecedingZero(num: number) {
    return ('0' + num).slice(-2);
}
//FIND ABSOLUTE DIFFERENCE
export function getAbsoluteChange(current: number, latest: number) {
    return Number(latest) - Number(current);
}

//PERCENTAGE CHANGE FUNCTION
export function getPercentageChange(current: number, previous: number) {
    //if (!current || !previous) return 0;
    const decimalNumber: number = ((current - previous) / current) * 100;
    return Math.round(decimalNumber * 10) / 10;
}

//PERCENTAGE CHANGE FUNCTION FOR SIMULATOR
export function getPercentageChangeV2(current: number, latest: number) {
    // if (!current || !latest && current!==0) return 0;
    let decimalNumber: number = ((Number(latest) - Number(current)) / Number(current)) * 100;
    if (isNaN(decimalNumber) || decimalNumber == Number.POSITIVE_INFINITY || decimalNumber == Number.NEGATIVE_INFINITY)
        decimalNumber = 0;
    return _.round(decimalNumber, 2);
}

//percentage change calculator without round method
export function getPercentageChangeV2Decimal(current: number, latest: number, decimal?: number) {
    // if (!current || !latest && current!==0) return 0;
    let decimalNumber: number = ((Number(latest) - Number(current)) / Number(current)) * 100;
    if (isNaN(decimalNumber) || decimalNumber == Number.POSITIVE_INFINITY || decimalNumber == Number.NEGATIVE_INFINITY)
        decimalNumber = 0;

    // If decimal is provided, round to that many places; otherwise return original precision
    if (decimal !== undefined) {
        return _.round(decimalNumber, decimal);
    }
    return decimalNumber;
}

//SCALE MINBOUND
export function scaleMinValue(scalePercentage: number, value?: number) {
    let val = value;
    if (val === null || val === undefined) return 0;
    if (val === 0) val = -1;
    if (val < 0) return val + (val * scalePercentage) / 100;
    return val - (val * scalePercentage) / 100;
}

//SCALE MAXBOUND
export function scaleMaxValue(scalePercentage: number, value?: number) {
    let val = value;

    if (val === null || val === undefined) return 0;
    if (val === 0) val = 1;
    if (val < 0) return val - (val * scalePercentage) / 100;
    return val + (val * scalePercentage) / 100;
}
//CHART STRING FORMATTER
export function formatString(string: string, limit: number) {
    return string?.length > limit ? `${string.slice(0, limit)}...` : string;
}

export function ellipseMiddleString(string: string, start: number, end: number) {
    return string.length <= start + end
        ? string
        : string.substring(0, start) + '...' + string.substring(string.length - end);
}

export const addLineAndFormatString = (str: string = '', limit: number) => {
    const halfLimit = Math.ceil(limit / 2);
    let newString = str.slice(0, halfLimit) + '\n';
    newString += str.length > limit ? '...' : '';
    let res: string = '';
    for (let i = 0; i < Math.min(str.length, limit); i++) {
        if (i === halfLimit) res += '\n' + str[i];
        else res += str[i];
    }
    if (limit < str.length) res += '...';
    else if (str.length <= halfLimit) res += '\n';
    return res;
};

//unit formatter
export function formatUnit(key: string | number, updateKey: boolean = false, config: any) {
    const { commonConfig } = config;
    const { currency, volume } = commonConfig.units;
    const tileLabelMapping: any = {
        abs: currency,
        percentage: '%',
        vol: volume,
    };
    let updatedKey = key;
    //use update key if more than 1 tile value is considered
    if (updateKey) {
        const labelTileMapping: any = {
            abs_vol: 'vol',
            abs_val: 'abs',
            percentage_vol: 'percentage',
            percentage_val: 'percentage',
            percentage_profit: 'percentage',
            percentage_units: 'percentage',
            abs_profit: 'abs',
            abs_frontline_sales: 'abs',
            percentage_frontline_sales: 'percentage',
            abs_net_sales: 'abs',
            percentage_net_sales: 'percentage',
        };
        updatedKey = labelTileMapping[key];
    }
    if (updatedKey in tileLabelMapping) return tileLabelMapping[updatedKey];
    return '';
}

//to give unit in return as the value
export function choseUnit(unit: any, value: any, numberFormateDecimal: any) {
    switch (unit) {
        case 'value':
            return value !== null ? _.round(value, numberFormateDecimal) : '';
        case '$':
            return value !== null ? `$${_.round(value, numberFormateDecimal)}` : '';
        case '£':
            return value !== null ? `£${_.round(value, numberFormateDecimal)}` : '';
        case '%':
            return value !== null ? `${_.round(value, numberFormateDecimal)}%` : '';
        case 'week':
            return value !== null ? `${_.round(value, numberFormateDecimal)}` : '';
    }
}

export const formatValue = (list: any, StringToShow: number) => {
    return list
        .slice(0, StringToShow)
        .map((item: any) => formatWord(item))
        .join(', ');
};

// //attribute types parsing (pass dimension object from filter context)
export const parseAttributeType = (dimension: any, excludedItems: string[] = [], excludedDimensions: any[] = []) => {
    const result: any = [];
    dimension.forEach((dimension: any) => {
        if (dimension.attribute?.length > 0) {
            dimension.attribute.forEach((item: any) => {
                if (
                    !excludedItems.includes(_.snakeCase(item.name)) &&
                    !excludedDimensions.includes(item.dimensionReference)
                ) {
                    const tempObj = {
                        dimension: item.dimensionReference,
                        label: item.name
                            ?.split('_')
                            ?.map((part: string) => part.charAt(0).toUpperCase() + part.slice(1))
                            ?.join(' '),
                        isAttribute: item.isAttribute,
                        value: result.length + 1,
                        key: item.name,
                    };
                    result.push(tempObj);
                }
            });
        }
    });
    return result;
};

type thresholdConfig = {
    optimal: thresholdItemType;
    over: thresholdItemType;
    under: thresholdItemType;
};

type thresholdItemType = {
    label: string;
    color: string;
};

//rateOptimalIndex
export const rateOptimalIndex = (threshold1: number, value: number, config: thresholdConfig, threshold2: number) => {
    if (value < threshold1) {
        return config.under;
    }
    if (value >= threshold1 && value < threshold2) {
        return config.optimal;
    }
    if (value >= threshold2) {
        return config.over;
    }
    return null;
};

export function calculateFontSize(screenSize: number, currentFontInPx: number, percentageIncrease?: number) {
    //resolution breakpoints for 2k and 4k
    const resolutionBreakpoints = [[2560, 3839], [3840]];

    const fontMapping: any = {
        5: [8, 14],
        10: [14, 18],
        11: [15, 25],
        13: [17, 27],
        15: [19, 29],
        17: [23, 33],
        18: [24, 34],
        19: [25, 37],
        23: [31, 45],
        31: [38, 45],
        40: [60, 80],
        50: [70, 90],
        60: [80, 100],
        64: [82, 124],
        90: [110, 130],
        95: [100, 115], // for bump reportapromo calendar legend
        100: [120, 140],
        120: [140, 165],
        130: [150, 170],
        140: [170, 190],
        148: [190, 280],
        160: [220, 240],
        185: [240, 360],
        200: [260, 300],
        210: [270, 310],
        250: [290, 340],
        300: [330, 360],
        310: [350, 380],
        600: [700, 800],
        1200: [1400, 1800],
    };

    //2K resolution
    if (screenSize >= resolutionBreakpoints[0][0] && screenSize <= resolutionBreakpoints[0][1])
        return currentFontInPx in fontMapping ? fontMapping[currentFontInPx][0] : currentFontInPx;

    //4k resolution
    if (screenSize >= resolutionBreakpoints[1][0])
        return currentFontInPx in fontMapping ? fontMapping[currentFontInPx][1] : currentFontInPx;
    return currentFontInPx;
}

export const calculateContainerHeight = (division: number = 2, buffer: number = 0) => {
    const headerNavHeight = calculateFontSize(window.innerWidth, 64);
    const sectionHeight = (window.innerHeight - headerNavHeight - buffer) / division;

    return sectionHeight;
};

type ResultData = {
    data: any[];
    string: string;
};

export const formatItemsOnCollapseState = (itemObj: any, key: string, length: number = 1) => {
    const res: ResultData = { data: [], string: '' };
    Object.keys(itemObj).forEach((item) => {
        if (String(item)?.toLowerCase() === key.toLowerCase()) {
            const items = Object.keys(itemObj[item]);

            //if no items present is present, it means all are selected
            if (items.length === 0) {
                res.data = [];
                res.string = 'Selected';
                return;
            }
            if (items.length <= length) {
                res.data = [];
                res.string = items.join(', ');
                return;
            }
            //if items present more than given limit, slice the string and show rest of the items on hover
            if (items.length > length) {
                res.data = items.slice(length);
                res.string = `${items.slice(0, length).join(',')} ... +${items.length - length} more`;
                return;
            }
        }
    });
    return res;
};

export const chooseIconIndex = () => {
    //resolution breakpoints for 2k and 4k
    const resolutionBreakpoints = [[2560, 3839], [3840]];
    const screenWidth = window.innerWidth;
    //2k resolution
    if (screenWidth >= resolutionBreakpoints[0][0] && screenWidth <= resolutionBreakpoints[0][1]) return 1;
    //4k resolution
    if (screenWidth >= resolutionBreakpoints[1][0]) return 2;
    return 0;
};

export function getAuthUser(userId?: string): string {
    try {
        const currentAuthUser = localStorage.getItem('email');
        if (typeof currentAuthUser === 'string') {
            const username = userId ?? currentAuthUser.split('@')[0];
            if (username.includes('.')) {
                const [firstName, lastName] = username.split('.');
                return `${firstName[0].toUpperCase()}${lastName[0].toUpperCase()}`;
            }
            return typeof username === 'string' ? username.slice(0, 2)?.toUpperCase() : 'PP';
        }

        return 'PP';
    } catch (error) {
        console.log({
            error,
        });
        return 'PP';
    }
}
export function extractEmailAndName() {
    // Find the position of the '@' symbol in the text
    try {
        let email = localStorage.getItem('email') as any;

        const namePart = email.split('@')[0];

        // Split the name using '.' and capitalize the first letter of each part
        const nameParts = namePart.split('.').map((part: any) => part.charAt(0).toUpperCase() + part.slice(1));

        // Join the name parts with a space to form the final name
        const fullName = nameParts.join(' ');

        return fullName;
    } catch (e) {
        console.log({
            e,
        });
        return 'User Name';
    }
}

export function restrictDecimalPlaces(input: string | number, decimalPlaces: number = 2) {
    const formattedString = String(input).split('.')[Math.max(0, decimalPlaces - 1)];
    if (formattedString?.length > 2) return true;
    return false;
}

export function fetchCurrency(config: any) {
    const { commonConfig } = config;
    const { currency, volume } = commonConfig.units;
    return currency;
}

export function fetchUnit(config: any, key: string = 'currency') {
    const { commonConfig } = config;
    const unit = commonConfig.units?.[key];
    return unit || '';
}

//get RBAC id
export function fetchScopeID(filterFormat: any): any {
    let obj = JSON.parse(localStorage.getItem('userAccessScope') || '{}')?.dimensions;
    let filterFormatCopy = _.cloneDeep(filterFormat);
    for (let dim in filterFormatCopy) {
        if (obj?.[dim]?.ids?.length) {
            filterFormatCopy[dim] = obj[dim];
        }
    }
    return JSON.stringify(filterFormatCopy);
}

export const restrictedCharsForInput = ['`', "'", '"', '\\', ';', '<'];

export const isWrongInput = (inputText: string) =>
    restrictedCharsForInput.some((char: string) => !!inputText.includes(char));

export function globalFilterFlattened(allCheckedAttribute: any) {
    try {
        const result: any = {};
        for (let i in allCheckedAttribute) {
            const attributeNames = Object.keys(allCheckedAttribute[i]);
            if (attributeNames.length > 0) {
                result[i] = [...attributeNames];
            }
        }
        return result;
    } catch (error) {
        throw error;
    }
}

export function convertGlobalFilterToHtml(filters: any) {
    let htmlData: string =
        '<div style="text-decoration:underline;font-weight:700;margin-bottom:10px;margin-top:10px;">Filtered by</div>';
    for (let i in filters) {
        const tempString: string = `<p><span style="font-weight:700">${_.startCase(i)}:  </span>${filters[i]?.join(
            ', ',
        )}</p>`;
        htmlData += tempString;
    }
    return htmlData;
}

export function blobToBase64(blob: any) {
    return new Promise((resolve, _) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(blob);
    });
}

export function getAdditionalDataOnCopyClick(
    allCheckedAttribute: any,
    timeSelectionLabel: any,
    timePeriodSelection: any,
    config: any,
    additionalDataCopy?: Record<string, string[]>,
) {
    let data = globalFilterFlattened(allCheckedAttribute) || {};
    let timestampRange = '';
    if (timePeriodSelection) {
        timestampRange = formatTimestamp(JSON.parse(timePeriodSelection || '{}'), config);
    }
    //adding time period to the filters object
    data = { ['Time Period']: [timeSelectionLabel + timestampRange], ...data, ...additionalDataCopy };
    const htmlData = convertGlobalFilterToHtml(data);
    return htmlData;
}

export function reverseObjectKeyValues(obj: any) {
    const temp: any = {};

    Object.keys(obj).forEach((key: string) => {
        temp[obj[key]] = key;
    });

    return temp;
}

export function sendNotification(
    type: 'error' | 'success' | 'info',
    description: string | ReactNode = '',
    duration: number = 5,
    width: number = 350,
) {
    const typeInfoMapping = {
        error: {
            icon: <CloseCircleFilled style={{ position: 'relative', top: -3, color: '#FF4D4F' }} />,
            message: 'Error',
        },
        success: {
            icon: <CheckCircleFilled style={{ position: 'relative', top: -3, color: '#52C41A' }} />,
            message: 'Success',
        },
        info: {
            icon: <ExclamationCircleFilled style={{ position: 'relative', top: -3, color: '#FAAD14' }} />,
            message: 'Info',
        },
    };
    notification[type]({
        duration: duration,

        icon: typeInfoMapping[type].icon,
        // message: 'Reset password link has been sent to your email.',
        message: typeInfoMapping[type].message,
        description: description,
        style: {
            width: width,
            height: 'auto',
            borderRadius: 8,
        },
    });
}

export const convertNumberStringIntoNumber = (data: any) => {
    if (typeof data === KeyTypes.OBJECT && data) {
        Object.keys(data).forEach((key) => {
            if (typeof data[key] === KeyTypes.OBJECT) {
                convertNumberStringIntoNumber(data[key]);
            }
            if (typeof data[key] === KeyTypes.STRING && !isNaN(data[key])) {
                data[key] = Number(data[key]);
            }
        });
    } else if (Array.isArray(data)) {
        data.forEach((key) => {
            convertNumberStringIntoNumber(key);
        });
    }
};

//function which finds intersection between new filter and existing filter
export function getIntersectionOnApplyFilter(filter: any, payload: any) {
    const tempApplyFilterResponse = JSON.parse(JSON.stringify(filter));
    let commonIdsDim1 = [];
    let commonIdsDim2 = [];
    let commonIdsDim3 = [];
    let commonIdsDim4 = [];
    if (payload?.dim1?.ids?.length > 0) {
        commonIdsDim1 = [...payload.dim1.ids];
        if (filter.dim1.ids?.length > 0) {
            //if no id in global filter , it means all are selected
            commonIdsDim1 = _.intersection(filter.dim1.ids, payload.dim1.ids);
        }
        tempApplyFilterResponse.dim1.ids = [...commonIdsDim1];
    }
    if (payload?.dim2?.ids?.length > 0) {
        commonIdsDim2 = [...payload.dim2.ids];
        if (filter.dim2.ids?.length > 0) {
            //if no id in global filter , it means all are selected
            commonIdsDim2 = _.intersection(filter.dim2.ids, payload.dim2.ids);
        }
        tempApplyFilterResponse.dim2.ids = [...commonIdsDim2];
    }
    if (payload?.dim3?.ids?.length > 0) {
        commonIdsDim3 = [...payload.dim3.ids];
        if (filter.dim3.ids?.length > 0) {
            //if no id in global filter , it means all are selected
            commonIdsDim3 = _.intersection(filter.dim3.ids, payload.dim3.ids);
        }
        tempApplyFilterResponse.dim3.ids = [...commonIdsDim3];
    }
    if (payload?.dim4?.ids?.length > 0) {
        commonIdsDim4 = [...payload.dim4.ids];
        if (filter.dim4.ids?.length > 0) {
            //if no id in global filter , it means all are selected
            commonIdsDim4 = _.intersection(filter.dim4.ids, payload.dim4.ids);
        }
        tempApplyFilterResponse.dim4.ids = [...commonIdsDim4];
    }
    return tempApplyFilterResponse;
}
//remove a specific string from a string
export const trimString = (string: string, toTrimString: string) => {
    return string?.replace(toTrimString, '');
};

// returns nearest 0.5 treshold value for given number
export const getPriceTresholdValue = (value: number) => {
    if (Math.ceil(value) === Math.ceil(value + 0.5)) {
        return Math.floor(value) + 0.5;
    }
    return Math.ceil(value);
};

export function getLastSixMonths() {
    var myVariable = new Date();
    var makeDate = new Date(myVariable);
    const dates = [];

    let count = 1;
    while (count <= 6) {
        const d = new Date(makeDate.setMonth(makeDate.getMonth() - count)).toString().split(' ');
        const monthDate = d[1] + ' ' + d[3];
        count += 1;
        dates.push(monthDate);
    }

    return dates;
}

//returns base url
export const getBaseUrl = () => {
    return process.env.REACT_APP_ENDPOINT_URL;
};

export const getScreenFilterDetails = (route: Location, globalFilterConfig: any) => {
    const globalFilterChangingRoutes = globalFilterConfig.globalFilterChangingRoutes;
    const screenName = route.pathname?.slice(route.pathname?.lastIndexOf('/') + 1);
    const screenFilterConfig = globalFilterChangingRoutes?.[screenName];
    const currentScreenFeatureName = screenFilterConfig?.dimensionListKey || 'default';
    return { screenName, screenFilterConfig, currentScreenFeatureName };
};

//generate applyfilter object from global filter
export const generateDefaultApplyFilter = (filter: any) => {
    let result: any = {};
    if (filter && Object.keys(filter)?.length > 0) {
        Object.keys(filter).forEach((data: any) => {
            result[`jsonstring_${data}`] = {};
        });
    }
    return result;
};

//delete __typename from object
export const deleteTypeNameFromResponse = (applyFilterResponse: any) => {
    if (applyFilterResponse && Object.keys(applyFilterResponse)?.length > 0) {
        for (let dim in applyFilterResponse) {
            delete applyFilterResponse[dim]['__typename'];
            delete applyFilterResponse[dim]['availableFrequencies'];
        }
    }
};

export function capitalizeFirstLetterOfEachWord(str: string, splitChar: string = ' ') {
    // Split the string into an array of words
    const words = str?.split(splitChar);

    // Capitalize the first letter of each word using lodash
    const capitalizedWords = words?.map((word) => {
        return word.charAt(0).toUpperCase() + word.slice(1);
    });

    // Join the capitalized words back into a string
    return capitalizedWords?.join(' ');
}

export const fetchTokenFromLocalStorage = () => {
    const accessToken = localStorage.getItem('accessToken');
    return accessToken;
};

export const clearLocalStorage = () => {
    // clear everything one by one
    localStorage.removeItem('awc.session.expiry');
    localStorage.removeItem('email');
    localStorage.removeItem('okta-shared-transaction-storage');
    localStorage.removeItem('userAccessScope');
    localStorage.removeItem('okta-token-storage');
    localStorage.removeItem('okta-cache-storage');
    localStorage.removeItem('awc.session.id');
    localStorage.removeItem('ajs_anonymous_id');
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('loglevel');
    // localStorage.setItem(
    //     'playbookKpiValues',
    //     JSON.stringify({
    //         GuidelinesTS: 9,
    //         GuidelinesFrequency1: 4,
    //         GuidelinesFrequency2: 6,
    //         GuidelinesROI: 2,
    //     }),
    // );
};
export const getAuthUserMail = (): string => {
    return localStorage.getItem('email') ?? '';
};

export const compareUsers = (user: string) => {
    return user && localStorage.getItem('email') === user;
};

export const generateErrorMsg = (errorMsg: string, defaultMsg?: string) => {
    const defaultMessage = defaultMsg ?? messageConfig.errorMessages.default;
    const errorMessage = errorMsg || defaultMessage;
    return errorMessage;
};

export const extractErrorMessage = (error: any): string => {
    const errorDetails = error?.response?.data?.detail;
    if (Array.isArray(errorDetails) && errorDetails.length > 0) {
        return errorDetails
            .map((d) => d?.description)
            .filter(Boolean)
            .join(', ');
    }

    return '';
};

export const formatTimestamp = (timestamp: { start_timestamp: number; end_timestamp: number }, config: any) => {
    const startTimestamp = timestamp?.start_timestamp;
    const endTimestamp = timestamp?.end_timestamp;
    const timestampRange = ` (${dayjs
        .unix(startTimestamp)
        // .add(utcStartOffsetOffset, 'm')
        .utc()
        //   .startOf('d')
        .format(config?.dateFormat)} - ${dayjs
        .unix(endTimestamp)
        // .add(utcEndOffsetOffset, 'm')
        .utc()
        // .startOf('d')
        .format(config?.dateFormat)})`;
    return timestampRange;
};

export const formatProjectInfo = (projectInfo: Project | null, scenarioInfo: Scenario | null) => {
    if (!projectInfo || !scenarioInfo) return [];
    const projectName = ['Project Name ', projectInfo?.projectName];
    const simulationDate = ['Simulation Period ', projectInfo?.simulationPeriod];
    const priceEffectiveDate = ['Price Effective Date ', projectInfo?.priceEffectiveDate];
    const dataSource = ['Data Source ', projectInfo?.dataSource];
    const scenarioName = ['Scenario Name ', scenarioInfo?.scenarioName];
    const projectFiltersDim1 = [projectInfo.dim1Filter?.attributeType, projectInfo.dim1Filter?.fullAttributeNames];
    const projectFiltersDim2 = [projectInfo.dim2Filter?.attributeType, projectInfo.dim2Filter?.fullAttributeNames];
    return [
        projectName,
        simulationDate,
        priceEffectiveDate,
        dataSource,
        scenarioName,
        projectFiltersDim1,
        projectFiltersDim2,
        [''],
    ];
};

export function formatDatePreservingTimeValues(unix: number) {
    //convert unix to local timestamp preserving time values
    return dayjs.unix(unix).utc().startOf('day').format('MM/DD/YYYY');
}

export function convertSetsToArraysAndStringify(data: any): string {
    const replacer = (key: string, value: any) => {
        if (value instanceof Set) {
            return {
                __type: 'Set',
                values: Array.from(value), // Store the values of the set in an array
            };
        }
        return value;
    };

    return JSON.stringify(data, replacer);
}

export function convertSetsToArrays(obj: any) {
    if (obj instanceof Set) {
        return Array.from(obj); // Convert Set to array
    } else if (typeof obj === 'object' && obj !== null) {
        // Recursively traverse each key in the object
        for (let key in obj) {
            obj[key] = convertSetsToArrays(obj[key]);
        }
    }
    return obj;
}

export function convertArraysToSets(obj: any) {
    if (Array.isArray(obj)) {
        return new Set(obj); // Convert array to set
    } else if (typeof obj === 'object' && obj !== null) {
        // Recursively traverse each key in the object
        for (let key in obj) {
            obj[key] = convertArraysToSets(obj[key]);
        }
    }
    return obj;
}

export function removeEmptyObjects(obj: any) {
    for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
            if (
                obj[key] && // Check if the value is not null or undefined
                typeof obj[key] === 'object' && // Check if the value is an object
                Object.keys(obj[key]).length === 0 // Check if the object is empty
            ) {
                delete obj[key]; // Remove the property if it's an empty object
            }
        }
    }
    return obj;
}
export const findAttributeTypeFromList = (attributeTypeList: any, attributeTypeKey: string) => {
    let selectedAttrType: any = null;
    attributeTypeList.forEach((item: any) => {
        item?.options?.forEach((attrType: any) => {
            if (attrType?.value === attributeTypeKey) {
                selectedAttrType = attrType;
                return;
            }

            if (selectedAttrType) return;
        });
    });
    return selectedAttrType;
};

export const removePropertyByKeyName = (obj: any, target: string) => {
    Object.keys(obj).forEach((key: string) => {
        if (key === target) {
            delete obj[key];
        } else if (typeof obj[key] === 'object' && !Array.isArray(obj[key])) {
            removePropertyByKeyName(obj[key], target);
        }
    });
};

export function truncateLabel(label = '', charLimit = 0) {
    return label.length > charLimit ? `${label.slice(0, charLimit)}...` : label;
}

//condition to check if token is invalid
//refresh page if token is invalid so that new token will be generated
//this will redirect user to login page if session is expired
export function handleTokenErrorAndRedirect(error: any) {
    if (error && error?.err_grp === 'AuthorizationError' && [400, 404].includes(error?.err_code)) {
        //this is to avoid multiple refreshes
        if (localStorage.getItem('tokenError')) {
            localStorage.clear();
        } else localStorage.setItem('tokenError', 'true');
        window.location.reload();
    }
}

//find tab from url
export function getTabFromUrl(tabs: tabsType[]) {
    return tabs.find((item) => location.pathname.includes(item?.url ?? 'null')) ?? tabs[0];
}

//'price_off_12pct_jan';
// 'combination_offer_buy_1_get_2pct_jan';
export function generateEventName(
    offer: string | any[],
    offerMethod: OfferMethod,
    startDate: string,
    offerType?: string,
    offerTypeUnit?: OfferTypeUnit,
) {
    const formattedMonth = startDate ? dayjs(startDate).format('MMM') : '';
    if (offerMethod === 'manual') {
        const numberPostString = offerTypeUnit === 'abs' ? '' : 'pct';
        return `${offerType}_${
            Array.isArray(offer)
                ? `buy_${offer?.[0]}${numberPostString}_get_${offer?.[1]}${numberPostString}_${formattedMonth}`
                : `${offer}${numberPostString}_${formattedMonth}`
        }`;
    }
    return `${offer}_${formattedMonth}`;
}

export function getScreenInfoFromUrl(url: string, screensConfig: any[]) {
    try {
        const lastWordFromUrl = url.split('/').pop();
        let parentLabel = '';
        let childLabel = '';
        for (const section of screensConfig) {
            // Search within options for a matching child route
            for (const key in section?.options) {
                let child = null;
                if (Array.isArray(section?.options?.[key])) {
                    child = section?.options?.[key]?.find((option: any) => option?.path === lastWordFromUrl);
                }
                if (child) {
                    return {
                        parentLabel: section?.label,
                        childLabel: child?.label,
                    };
                }
            }
        }
        return {
            parentLabel,
            childLabel,
        };
    } catch (error) {
        return {
            parentLabel: '',
            childLabel: '',
        };
    }
}

export function getAccessIcon(isPrivate: boolean, style?: React.CSSProperties) {
    return isPrivate ? (
        <Tooltip title="Marked as private. Visible only to you." placement="bottomRight">
            <LockOutlined
                style={{
                    position: 'relative',
                    top: -3,
                    marginLeft: 4,
                    fontSize: 16,
                    color: colorConstants.greyAlt15,
                    ...style,
                }}
            />
        </Tooltip>
    ) : null;
}

export function formatNumbers(
    number: number,
    pre: string,
    post: string,
    decimalForNonPercentage: number,
    decimalForPercentage: number,
    useCommas: boolean = false,
) {
    return (
        pre +
        (useCommas
            ? formatNumberWithCommas(number, decimalForNonPercentage)
            : formatNumWithKMBAlt(
                  number,
                  post === '%' ? decimalForPercentage : decimalForNonPercentage,
                  post === '%' ? undefined : decimalForNonPercentage,
              )) +
        post
    );
}

/**
 * Formats a number with comma separators and fixed decimal places.
 *
 * @param {number} value - The number to format.
 * @param {number} allowedDecimals - Number of decimal places to include.
 * @returns {string} - Formatted string like "45,628.00"
 */
export function formatNumberWithCommas(value: number, allowedDecimals: number) {
    return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: allowedDecimals,
        maximumFractionDigits: allowedDecimals,
    }).format(value);
}

export function downloadCsv(blobPart: any[], fileName: string) {
    // Create a Blob from the CSV data
    const blob = new Blob(blobPart, { type: 'text/csv' });

    // Create a download link
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName); // Set file name
    document.body.appendChild(link);
    link.click();

    // Clean up and remove the link
    link.parentNode?.removeChild(link);
}

export function sanitizeWorksheet(ws: any) {
    if (!ws['!ref']) return;

    const range = ws['!ref'].split(':');
    // iterate all cells present in ws
    Object.keys(ws).forEach((addr) => {
        if (addr[0] === '!') return; // metadata keys

        const cell = ws[addr];
        if (!cell) return;

        // If numeric but not finite -> blank it
        if (cell.t === 'n' && !Number.isFinite(cell.v)) {
            cell.t = 's';
            cell.v = '';
            return;
        }

        // If string -> strip illegal control chars
        if (cell.t === 's' && typeof cell.v === 'string') {
            cell.v = cell.v.replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g, '');
            // Excel cell max length
            if (cell.v.length > 32767) cell.v = cell.v.slice(0, 32767);
        }
    });
}

export async function downloadXlsx(csvData: string, fileName: string) {
    try {
        // Validate input
        if (!csvData) {
            throw new Error('No Data');
        }

        // Dynamic import for XLSX library
        const XLSX = await import('xlsx');

        // Parse CSV data into array of arrays and coerce numeric-looking cells to numbers
        const lines = csvData.split(/\r?\n/);
        const data = lines.map((line) =>
            line.split(',').map((cell) => {
                const trimmed = cell.trim();
                // Coerce to number only when result is finite (IDs like "719e8882" parse as Infinity → keep as string)
                if (trimmed !== '') {
                    const num = Number(trimmed);
                    if (Number.isFinite(num)) return num;
                }
                return trimmed;
            }),
        );

        // Create a new workbook
        const wb = XLSX.utils.book_new();

        // Convert array of arrays into a worksheet
        const ws = XLSX.utils.aoa_to_sheet(data);

        // Append worksheet to workbook
        XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');

        // Write workbook and create blob
        const wBook = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([wBook], { type: 'application/octet-stream' });

        // Create download link
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${fileName}.xlsx`);
        document.body.appendChild(link);
        link.click();

        // Clean up
        link.parentNode?.removeChild(link);
        window.URL.revokeObjectURL(url);
    } catch (error) {
        console.error('Error downloading XLSX:', error);
        throw error;
    }
}

//transforms filter to updated filters object
export function transformFiltersV2(
    applyFilterObject: Record<string, any>,
    filterConfig: any[],
    skipAttributeType?: string,
) {
    const updatedFilters: Record<string, any> = {};

    // Initialize dimensions
    filterConfig.forEach(({ dimension }) => {
        if (!updatedFilters[dimension]) {
            updatedFilters[dimension] = { inclusion: [], exclusion: [] };
        }
    });

    // Process each attribute in input1
    Object.entries(applyFilterObject).forEach(([attributeType, attributeValues]) => {
        const attributeMeta = filterConfig.find((item) => item.value === attributeType);

        if (!attributeMeta) return; // skip if no mapping found

        const { dimension } = attributeMeta;

        // Skip if matches skipAttributeType
        if (attributeType === skipAttributeType) return;

        if (attributeValues.inclusion) {
            attributeValues.inclusion.forEach((value: string) => {
                updatedFilters[dimension].inclusion.push({
                    attributeType,
                    attributeExId: value,
                    attributeDesc: value,
                    dimension,
                });
            });
        }

        if (attributeValues.exclusion) {
            attributeValues.exclusion.forEach((value: string) => {
                updatedFilters[dimension].exclusion.push({
                    attributeType,
                    attributeExId: value,
                    attributeDesc: value,
                    dimension,
                });
            });
        }
    });

    return updatedFilters;
}

//convert array to string, add + more if it exceeds maxlength
export function arrayToString(array: string[], maxLength: number) {
    if (!Array.isArray(array)) {
        throw new Error('Input must be an array');
    }

    const joinedString = array.slice(0, maxLength).join(', ');
    const remainingCount = array.length - maxLength;

    if (remainingCount > 0) {
        return `${joinedString}, +${remainingCount} more`;
    } else {
        return joinedString;
    }
}
export function storeTokenInLocalStorage(authResult: Auth0DecodedHash) {
    localStorage.setItem('accessToken', authResult.accessToken ?? '');
    localStorage.setItem('idToken', authResult.idToken ?? '');
    localStorage.setItem('idTokenPayload', JSON.stringify(authResult.idTokenPayload));
}

export type KpiAttributeConfig = {
    allowIncrementChange: boolean;
    allowAbsChange: boolean;
    allowFinalValueChange: boolean;
    allowedDecimals: number;
    hasKpiRulesValidations: boolean;
    allowNegative: boolean;
};

export const getKpiAttributeConfig = (kpiId: number, lenseConfig: LenseConfig[]): KpiAttributeConfig | null => {
    for (const lens of lenseConfig) {
        for (const attr of lens.attributes) {
            if (attr.attributeId === kpiId) {
                return {
                    allowIncrementChange: attr.allowIncrementChange ?? true,
                    allowAbsChange: attr.allowAbsChange ?? true,
                    allowFinalValueChange: attr.allowFinalValueChange ?? true,
                    allowedDecimals: attr.allowedDecimals ?? 0,
                    hasKpiRulesValidations: attr.hasKpiRulesValidations ?? false,
                    allowNegative: attr.allowNegative ?? true,
                };
            }
        }
    }
    return null;
};

export function transformFiltersV3(
    applyFilterObject: Record<string, any>,
    filterConfig: any[],
    skipAttributeType?: string,
) {
    const updatedFilters: Record<string, any> = {};
    // Initialize dimensions
    filterConfig.forEach(({ value }) => {
        if (!updatedFilters[value]) {
            updatedFilters[value] = { inclusion: [], exclusion: [] };
        }
    });

    // Process each attribute in input1
    Object.entries(applyFilterObject).forEach(([attributeType, attributeValues]) => {
        const attributeMeta = filterConfig.find((item) => item.value === attributeType);

        if (!attributeMeta) return; // skip if no mapping found

        const { value: keyName } = attributeMeta;

        // Skip if matches skipAttributeType
        if (attributeType === skipAttributeType) return;

        if (attributeValues.inclusion) {
            attributeValues.inclusion.forEach((value: string) => {
                updatedFilters[keyName].inclusion.push(`${value}`);
            });
        }

        if (attributeValues.exclusion) {
            attributeValues.exclusion.forEach((value: string) => {
                updatedFilters[keyName].exclusion.push(`${value}`);
            });
        }
    });

    return updatedFilters;
}
export const filterOutDisplayOnlyAttributes = (lensConfig: LenseConfig[]) => {
    return (
        lensConfig?.map((lens) => ({
            ...lens,
            attributes: lens.attributes?.filter((attr) => attr.kpiTypeId !== KpiTypeId.DISPLAY_ONLY) || [],
        })) || []
    );
};

//update time periods label coming from backend with custom labels
export function updateTimePeriodLabels(inputObj: Record<string, any>, replacements: Record<string, string>) {
    const updatedObj: Record<string, any> = {};

    for (const key in inputObj) {
        updatedObj[key] = inputObj[key]?.map((item: any) => {
            if (item && typeof item === 'object' && 'timePeriod' in item) {
                const updatedTimePeriodLabel = getReplacementTimePeriodLabel(
                    item.timePeriod?.toUpperCase(),
                    replacements,
                );
                return {
                    ...item,
                    timePeriod: updatedTimePeriodLabel,
                };
            }
            return item;
        });
    }

    return updatedObj;
}

export function getReplacementTimePeriodLabel(oldLabel: string, replacementKeyMapping: Record<string, string>) {
    const updatedTimePeriodLabel = replacementKeyMapping?.[oldLabel] || oldLabel;
    return updatedTimePeriodLabel;
}

export function getKeyByValue(obj: Record<string, any>, targetValue: string) {
    return Object.keys(obj ?? {}).find((key) => obj[key] === targetValue) || targetValue;
}

/**
 * Sorts an array of objects based on a preferred order of values.
 *
 * @param items - The array of objects to sort
 * @param preferredOrder - Array of preferred values in desired order
 * @param key - The object property to use for comparison
 * @returns A new sorted array
 */
export function sortByPreferredOrder<T, K extends keyof T>(
    items: T[],
    preferredOrder: (T[K] & (string | number))[],
    key: K,
): T[] {
    const orderMap = new Map<T[K], number>(preferredOrder.map((value, index) => [value, index]));

    return [...items].sort((a, b) => {
        const aRank = orderMap.has(a[key]) ? orderMap.get(a[key])! : Infinity;
        const bRank = orderMap.has(b[key]) ? orderMap.get(b[key])! : Infinity;
        return aRank - bRank;
    });
}

export function sanitizeFileName(name: string, extension = '.xlsx') {
    return (
        name
            .replace(/[<>:"/\\|?*]/g, '') // Remove illegal characters
            .replace(/[$()'`]/g, '') // Remove confusing ones
            .replace(/\s+/g, '_') // Replace spaces with underscores
            .replace(/\.+/g, '.') // Clean up multiple dots
            .replace(/\.$/, '') + // Strip trailing dot
        (name.endsWith(extension) ? '' : extension) // Ensure correct extension
    );
}

export const getDeleteButtonState = (
    status: string | undefined,
    craStartDate: number | null | undefined,
    deleteAllowedStatus: string[],
    is_ever_successfully_published: boolean,
    promoType: number | null | undefined,
) => {
    if (promoType === PromotionTypes.Post_Audit || promoType === PromotionTypes.Write_Off) {
        return {
            isDisabled: true,
            tooltip: 'Delete is not allowed for post audit or write off promos.',
        };
    }
    // Check if status is not in deleteAllowedStatus
    if (!deleteAllowedStatus.includes(status as string)) {
        return {
            isDisabled: true,
            tooltip: `Delete is allowed only when the status is ${deleteAllowedStatus
                .map((item: string) => formatWord(item))
                .join(', ')}.`,
        };
    }

    // Check if published
    if (isEverSuccessfullyPublished(status as string) || is_ever_successfully_published) {
        // If CRA end date is in the past, disable delete
        if (craStartDate && dayjs(craStartDate).isBefore(dayjs())) {
            return {
                isDisabled: true,
                tooltip: 'Delete is not allowed for promos with CRA start date in the past.',
            };
        }
        // Otherwise, allow delete (CRA date is in future or today)
        return {
            isDisabled: false,
            tooltip: '',
        };
    }

    // If never published, allow delete
    return {
        isDisabled: false,
        tooltip: '',
    };
};

// Function to determine edit button disabled state and tooltip based on conditions
export const getEditButtonState = (planStatus: string | undefined, selectedEventInfo: any, craStartDate: any) => {
    const is_ever_successfully_published = selectedEventInfo?.is_ever_successfully_published || false;
    const promoStatus = selectedEventInfo?.currentPromoTactic?.status?.toLowerCase() as string;
    const promoAllowedStatusWhenPlanNotApproved = ['draft', 'planned'];
    const promoAllowedStatusWhenPlanApproved = [
        'draft',
        'planned',
        'approved',
        'customer_accepted',
        'customer accepted',
        'rejected',
        'executed',
    ];
    const promoType = selectedEventInfo?.currentPromoTactic?.promoType;
    if (promoType === PromotionTypes.Post_Audit || promoType === PromotionTypes.Write_Off) {
        return {
            isDisabled: true,
        };
    }

    // If plan status is NOT "approved"
    if (planStatus == 'approved') {
        // Check if promo status is in allowed list when plan is not approved
        if (promoAllowedStatusWhenPlanApproved.includes(promoStatus as string)) {
            // If promo was published at least once
            if (isEverSuccessfullyPublished(promoStatus as string) || is_ever_successfully_published) {
                if (craStartDate && dayjs(craStartDate).isBefore(dayjs())) {
                    return {
                        isDisabled: true,
                    };
                } else {
                    // Otherwise, allow edit (CRA date is in future or today)
                    return {
                        isDisabled: false,
                    };
                }
            } else {
                return {
                    isDisabled: false,
                };
            }
        } else {
            return {
                isDisabled: true,
            };
        }
    } else {
        if (promoAllowedStatusWhenPlanNotApproved.includes(promoStatus as string)) {
            if (isEverSuccessfullyPublished(promoStatus as string) || is_ever_successfully_published) {
                if (craStartDate && dayjs(craStartDate).isBefore(dayjs())) {
                    return {
                        isDisabled: true,
                    };
                } else {
                    return {
                        isDisabled: false,
                    };
                }
            } else {
                return {
                    isDisabled: false,
                };
            }
        } else {
            return {
                isDisabled: true,
            };
        }
    }
};

export const isEverSuccessfullyPublished = (status: string) => {
    if (!['draft', 'planned', 'submitted_for_approval'].includes(status)) {
        return true;
    }
    return false;
};

export function subtractYear(startTimestamp: number, endTimestamp: number) {
    const start = dayjs.unix(startTimestamp);
    const end = dayjs.unix(endTimestamp);

    const yaStart = start.subtract(1, 'year');
    const yaEnd = end.subtract(1, 'year');

    return {
        start_timestamp: yaStart.unix(),
        end_timestamp: yaEnd.unix(),
    };
}

// Function to subtract two arrays of arrays (cy - ya)
export const subtractArraysOfArrays = (cy: any[][], ya: any[][]): any[][] => {
    const result: any[][] = [];

    cy.forEach((cyRow, rowIndex) => {
        const yaRow = ya[rowIndex];
        const resultRow: any[] = [];

        cyRow.forEach((cyValue, colIndex) => {
            const yaValue = yaRow?.[colIndex];

            // If cy value is a string, concatenate with ya value using hyphen
            if (typeof cyValue === 'string') {
                resultRow.push(cyValue);
                resultRow.push(yaValue);
            }
            // If both values are numbers, perform subtraction
            else if (typeof cyValue === 'number' && typeof yaValue === 'number') {
                resultRow.push(cyValue - yaValue);
            }
            // If ya value doesn't exist or is not a number, return cy value
            else if (typeof cyValue === 'number') {
                resultRow.push(cyValue);
            }
            // For any other case, return cy value as is
            else {
                resultRow.push(cyValue);
            }
        });

        result.push(resultRow);
    });

    return result;
};

export function parseSliderValue(value: number | string) {
    if (Number(value) === 32) {
        return 26;
    }
    return Number(value);
}

export const formatFrequencyMapping = (commonConfig: any): Record<string, string> => {
    return {
        M: 'MMM YYYY',
        Q: 'Q YYYY',
        W: commonConfig?.dateFormatAlt,
    };
};
